import { toTextContent } from '@principle-theorem/editor';
import {
  IInteraction,
  InteractionType,
  IPendingAssignmentNotification,
  IPractice,
  IPracticeSetupStep,
  IPracticeSetupWizard,
  IStaffer,
  PracticeCollection,
  PracticeSetupStepId,
  PracticeSetupStepStatus,
  PracticeSetupWizardCollection,
  PracticeSetupWizardStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForEach,
  AtLeast,
  collectionGroupQuery,
  CollectionReference,
  DocumentReference,
  Firestore,
  INamedDocument,
  IReffable,
  isSameRef,
  orderBy,
  Region,
  snapshot,
  subCollection,
  Timestamp,
  toJSON,
  toTimestamp,
  Transaction,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { where } from 'firebase/firestore';
import { of } from 'rxjs';
import { stafferToNamedDoc } from '../common';
import { Interaction } from '../interaction/interaction';
import { OrganisationCache } from '../organisation/organisation-cache';
import {
  IPracticeSetupStepDefinition,
  IStepValidationOptions,
  IValidationResult,
} from './practice-setup-steps/practice-setup-step.types';
import { PRACTICE_SETUP_STEPS } from './practice-setup-steps/practice-setup-steps';
import { PracticeSetupWizardStepResync } from './practice-setup-wizard-step-resync';

export class PracticeSetupWizard {
  static init(
    overrides: AtLeast<IPracticeSetupWizard, 'practiceRef' | 'clickUpTaskId'>
  ): IPracticeSetupWizard {
    return {
      status: PracticeSetupWizardStatus.NotStarted,
      deleted: false,
      lastEngagementByStaffer: {},
      disengagementNotifiedAt: {},
      assignedStaffAt: {},
      lastDailyCheckSentAt: {},
      lastWeeklyDigestSentAt: {},
      ...overrides,
    };
  }

  /**
   * Creates all setup steps for a wizard based on the region-filtered step
   * definitions. Each step is written to Firestore as a sub-document under the
   * wizard, using the step's `id` as the document key to ensure idempotency.
   * An "initialized" interaction is also recorded for audit purposes.
   *
   * When a `transaction` is provided, all writes participate in that
   * transaction — callers are responsible for ensuring the transaction is
   * committed. Without a transaction, each write is independent (the caller
   * should consider atomicity implications).
   */
  static async initialiseSteps(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    region: Region,
    transaction?: Transaction
  ): Promise<void> {
    const wizard = { ref: wizardRef };
    const steps = PracticeSetupWizard.getRegionFilteredSteps(region).map(
      (definition): IPracticeSetupStep => ({
        id: definition.id,
        category: definition.category,
        title: definition.title,
        description: definition.description,
        status: PracticeSetupStepStatus.NotStarted,
        validationPassed: false,
        deleted: false,
      })
    );

    await asyncForEach(steps, async (step) =>
      Firestore.addDoc(
        PracticeSetupWizard.stepCol(wizard),
        step,
        step.id,
        transaction
      )
    );

    const interaction = Interaction.init({
      title: [toTextContent('Setup wizard initialized')],
      type: InteractionType.TaskCreate,
    });

    await Firestore.addDoc(
      PracticeSetupWizard.interactionCol(wizard),
      interaction,
      undefined,
      transaction
    );
  }

  static async activeWizards(): Promise<WithRef<IPracticeSetupWizard>[]> {
    const allWizards = await Firestore.getDocs(
      collectionGroupQuery<IPracticeSetupWizard>(PracticeCollection.SetupWizard)
    );

    return allWizards
      .filter(undeletedFilter)
      .filter((wizard) => wizard.status !== PracticeSetupWizardStatus.Complete);
  }

  static async dismiss(
    wizardRef: DocumentReference<IPracticeSetupWizard>
  ): Promise<void> {
    await Firestore.patchDoc(wizardRef, {
      dismissedAt: toTimestamp(),
    });
  }

  static async getByPractice(
    practice: IReffable<IPractice>
  ): Promise<WithRef<IPracticeSetupWizard> | undefined> {
    return Firestore.firstResult(
      PracticeSetupWizard.col(practice),
      orderBy('createdAt', 'desc')
    );
  }

  static col(
    practice: IReffable<IPractice>
  ): CollectionReference<IPracticeSetupWizard> {
    return subCollection<IPracticeSetupWizard>(
      practice.ref,
      PracticeCollection.SetupWizard
    );
  }

  static stepCol(
    wizard: IReffable<IPracticeSetupWizard>
  ): CollectionReference<IPracticeSetupStep> {
    return subCollection<IPracticeSetupStep>(
      wizard.ref,
      PracticeSetupWizardCollection.Steps
    );
  }

  static interactionCol(
    wizard: IReffable<IPracticeSetupWizard>
  ): CollectionReference<IInteraction> {
    return subCollection<IInteraction>(
      wizard.ref,
      PracticeSetupWizardCollection.Interactions
    );
  }

  static pendingNotificationCol(
    wizard: IReffable<IPracticeSetupWizard>
  ): CollectionReference<IPendingAssignmentNotification> {
    return subCollection<IPendingAssignmentNotification>(
      wizard.ref,
      PracticeSetupWizardCollection.PendingAssignmentNotifications
    );
  }

  /**
   * Assigns a staffer to a setup step within a Firestore transaction. Locates
   * the step by `stepId`, sets the `assignedUser` and `dueDate`, and records
   * an assignment interaction attributed to `assignedBy`.
   *
   * @throws If the step cannot be found or has been soft-deleted.
   */
  static async assignStep(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stepId: PracticeSetupStepId,
    assignedUser: INamedDocument<IStaffer>,
    dueDate: Timestamp,
    assignedBy: WithRef<IStaffer>
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const steps = await Firestore.getDocs(
        PracticeSetupWizard.stepCol(asReffable(wizardRef)),
        where('id', '==', stepId)
      );
      const step = steps
        .filter(undeletedFilter)
        .find(({ id }) => id === stepId);

      if (!step) {
        throw new Error(`Step with ID ${stepId} not found`);
      }

      await Firestore.updateDoc(
        step.ref,
        {
          assignedUser,
          dueDate,
        },
        transaction
      );

      const interaction = Interaction.init({
        title: [
          toTextContent(`Step ${stepId} assigned to ${assignedUser.name}`),
        ],
        type: InteractionType.TaskAssign,
        owner: stafferToNamedDoc(assignedBy),
      });

      await Firestore.addDoc(
        PracticeSetupWizard.interactionCol(asReffable(wizardRef)),
        interaction,
        undefined,
        transaction
      );
    });
  }

  /**
   * Marks a setup step as complete within a Firestore transaction, enforcing
   * dependency and validation constraints.
   *
   * When `overrideCompleted` is false (the default), the method:
   * 1. Verifies all steps listed in the definition's `dependsOn` are complete.
   * 2. Runs the step's validation function against the current practice state.
   * 3. If validation fails, transitions the step to `ValidationFailed` and
   *    throws with the validation reason.
   *
   * On success the step is moved to `Complete`, a completion interaction is
   * logged, and {@link assesWizardProgress} is called to potentially
   * transition the wizard itself to `Complete` or `InProgress`.
   *
   * @throws If the step is not found, its definition is missing, dependencies
   *         are incomplete, or validation fails.
   */
  static async completeStep(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stepId: PracticeSetupStepId,
    completedBy: WithRef<IStaffer>,
    region: Region,
    validationOptions?: IStepValidationOptions,
    overrideCompleted = false
  ): Promise<void> {
    const result = await Firestore.runTransaction(async (transaction) => {
      const steps = await Firestore.getDocs(
        PracticeSetupWizard.stepCol(asReffable(wizardRef))
      );
      const step = steps
        .filter(undeletedFilter)
        .find(({ id }) => id === stepId);
      if (!step) {
        throw new Error(`Step with ID ${stepId} not found`);
      }

      const stepDefinition = PracticeSetupWizard.findStepDefinition(
        stepId,
        region
      );
      if (!stepDefinition) {
        throw new Error(`Step definition not found for ${stepId}`);
      }

      if (!overrideCompleted) {
        if (stepDefinition.dependsOn.length > 0) {
          const incompleteDeps = stepDefinition.dependsOn.filter(
            (dependencyId) => {
              const dependedOnStep = steps.find(
                ({ id }) => id === dependencyId
              );
              return (
                !dependedOnStep ||
                dependedOnStep.status !== PracticeSetupStepStatus.Complete
              );
            }
          );

          if (incompleteDeps.length) {
            throw new Error(
              `Cannot complete step "${
                stepDefinition.title
              }": dependencies not met. Required: ${incompleteDeps.join(', ')}`
            );
          }
        }

        const wizardDoc = await Firestore.getDoc(wizardRef, transaction);
        const practice = await OrganisationCache.practices.getDoc(
          wizardDoc.practiceRef
        );

        const validationResult = await PracticeSetupWizard.validateStep(
          stepId,
          practice,
          region,
          step,
          validationOptions
        );

        if (!validationResult.valid) {
          await Firestore.patchDoc(
            step.ref,
            {
              status: PracticeSetupStepStatus.ValidationFailed,
              validationPassed: false,
              validationErrors: validationResult.reason
                ? [validationResult.reason]
                : ['Validation failed'],
            },
            transaction
          );
          return {
            success: false,
            reason:
              validationResult.reason || `Validation failed for step ${stepId}`,
          };
        }
      }

      await Firestore.patchDoc(
        step.ref,
        {
          status: PracticeSetupStepStatus.Complete,
          validationPassed: true,
          completedAt: toTimestamp(),
          completedBy: stafferToNamedDoc(completedBy),
        },
        transaction
      );

      const interaction = Interaction.init({
        title: [toTextContent(`Step "${step.title}" completed`)],
        type: InteractionType.TaskComplete,
        owner: stafferToNamedDoc(completedBy),
      });

      await Firestore.addDoc(
        PracticeSetupWizard.interactionCol(asReffable(wizardRef)),
        interaction,
        undefined,
        transaction
      );

      const updatedSteps = steps.map((updatedStep) =>
        updatedStep.id === stepId
          ? {
              ...updatedStep,
              status: PracticeSetupStepStatus.Complete,
              validationPassed: true,
            }
          : updatedStep
      );

      await PracticeSetupWizard.assesWizardProgress(
        wizardRef,
        updatedSteps,
        region,
        transaction
      );

      return { success: true };
    });

    if (!result.success) {
      throw new Error(result.reason);
    }
  }

  /**
   * Reverts a completed step back to `InProgress` within a Firestore
   * transaction. Clears `completedAt` and `completedBy`, resets validation,
   * and re-evaluates wizard-level progress — which may transition the wizard
   * from `Complete` back to `InProgress`.
   *
   * @throws If the step is not found or is not in `Complete` status.
   */
  static async reopenStep(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stepId: PracticeSetupStepId,
    region: Region
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const steps = await Firestore.getDocs(
        PracticeSetupWizard.stepCol(asReffable(wizardRef))
      );
      const step = steps
        .filter(undeletedFilter)
        .find(({ id }) => id === stepId);
      if (!step) {
        throw new Error(`Step with ID ${stepId} not found`);
      }
      if (step.status !== PracticeSetupStepStatus.Complete) {
        throw new Error(`Only completed steps can be reopened`);
      }

      await Firestore.updateDoc(
        step.ref,
        {
          status: PracticeSetupStepStatus.InProgress,
          validationPassed: false,
          completedAt: undefined,
          completedBy: undefined,
        },
        transaction
      );

      const updatedSteps = steps.map((updatedStep) =>
        updatedStep.id === stepId
          ? {
              ...updatedStep,
              status: PracticeSetupStepStatus.InProgress,
              validationPassed: false,
            }
          : updatedStep
      );

      await PracticeSetupWizard.assesWizardProgress(
        wizardRef,
        updatedSteps,
        region,
        transaction
      );
    });
  }

  /**
   * Derives the wizard's aggregate status from the current state of its
   * region-relevant steps. Only non-deleted steps whose IDs appear in the
   * region-filtered definitions are considered:
   *
   * - **All complete** → wizard transitions to `Complete` with a timestamp.
   * - **Some in-progress or complete** → wizard transitions to `InProgress`.
   * - Otherwise no status change is written.
   *
   * Accepts a pre-fetched `steps` array so callers inside a transaction can
   * pass an optimistically-updated snapshot without an extra read.
   */
  static async assesWizardProgress(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    steps: WithRef<IPracticeSetupStep>[],
    region: Region,
    transaction?: FirebaseFirestore.Transaction
  ): Promise<void> {
    const regionStepIds = new Set(
      PracticeSetupWizard.getRegionFilteredSteps(region).map((def) => def.id)
    );
    const relevantSteps = steps
      .filter(undeletedFilter)
      .filter((updatedStep) => regionStepIds.has(updatedStep.id));
    const allComplete = relevantSteps.every(
      ({ status }) => status === PracticeSetupStepStatus.Complete
    );
    const someProgress = relevantSteps.some(
      ({ status }) =>
        status === PracticeSetupStepStatus.InProgress ||
        status === PracticeSetupStepStatus.Complete
    );

    if (allComplete) {
      await Firestore.patchDoc(
        wizardRef,
        {
          status: PracticeSetupWizardStatus.Complete,
          completedAt: toTimestamp(),
        },
        transaction
      );
      return;
    }

    if (someProgress) {
      await Firestore.patchDoc(
        wizardRef,
        {
          status: PracticeSetupWizardStatus.InProgress,
        },
        transaction
      );
    }
  }

  /**
   * Records a page visit against a setup step, de-duplicating by route + user
   * so each staffer/route combination is tracked at most once.
   *
   * Within a transaction the method also:
   * - Transitions the step from `NotStarted` → `InProgress` on first visit.
   * - Runs the step's validation function and persists the result.
   * - Re-evaluates overall wizard progress via {@link assesWizardProgress}.
   *
   * Silently no-ops when the wizard is already complete, the step is already
   * complete, or a duplicate visit is detected. All errors are caught and
   * swallowed to prevent visit tracking from disrupting the user's navigation.
   */
  static async trackVisit(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    stepId: PracticeSetupStepId,
    route: string,
    visitedBy: INamedDocument<IStaffer>,
    region: Region
  ): Promise<void> {
    try {
      const wizard = await Firestore.safeGetDoc(wizardRef);
      if (!wizard || wizard.status === PracticeSetupWizardStatus.Complete) {
        return;
      }

      const steps = await Firestore.getDocs(
        PracticeSetupWizard.stepCol(asReffable(wizardRef)),
        where('id', '==', stepId)
      );

      const foundStep = steps
        .filter(undeletedFilter)
        .find(({ id }) => id === stepId);
      if (!foundStep) {
        return;
      }

      await Firestore.runTransaction(async (transaction) => {
        const step = await Firestore.getDoc(foundStep.ref, transaction);

        if (step.status === PracticeSetupStepStatus.Complete) {
          return;
        }

        const existingVisit = step.pageVisits?.find(
          (visit) =>
            visit.route === route && isSameRef(visit.visitedBy, visitedBy)
        );
        if (existingVisit) {
          return;
        }

        const pageVisits = step.pageVisits || [];
        pageVisits.push({
          route,
          visitedAt: toTimestamp(),
          visitedBy,
        });

        const updates: Partial<IPracticeSetupStep> = { pageVisits };

        if (step.status === PracticeSetupStepStatus.NotStarted) {
          updates.status = PracticeSetupStepStatus.InProgress;
        }

        const stepDefinition = PracticeSetupWizard.findStepDefinition(
          stepId,
          region
        );
        if (stepDefinition) {
          const wizardDoc = await Firestore.getDoc(wizardRef, transaction);
          const practice = await OrganisationCache.practices.getDoc(
            wizardDoc.practiceRef
          );
          const updatedStep = { ...step, ...updates };
          const validationResult = await snapshot(
            stepDefinition.validate$(of(practice), updatedStep)
          );

          if (validationResult) {
            updates.validationPassed = validationResult.valid;
            updates.validationErrors = validationResult.valid
              ? undefined
              : validationResult.reason
                ? [validationResult.reason]
                : ['Validation failed'];
          }
        }

        await Firestore.updateDoc(step.ref, updates, transaction);

        const allSteps = await Firestore.getDocs(
          PracticeSetupWizard.stepCol(asReffable(wizardRef))
        );
        const updatedSteps = allSteps.map((updatedStep) =>
          updatedStep.id === stepId
            ? {
                ...updatedStep,
                ...updates,
              }
            : updatedStep
        );

        await PracticeSetupWizard.assesWizardProgress(
          wizardRef,
          updatedSteps.filter(undeletedFilter),
          region,
          transaction
        );
      });
    } catch {
      return;
    }
  }

  /**
   * Batch-validates every non-complete, non-deleted step in the wizard against
   * the current practice state. Only writes to Firestore when a step's
   * validation result has actually changed (dirty-check via JSON comparison),
   * minimising unnecessary writes.
   *
   * Steps that throw during validation are marked as failed with a generic
   * error message, but only if they don't already carry validation errors —
   * preventing repeated overwrites on persistent failures.
   */
  static async validateAllSteps(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    region: Region
  ): Promise<void> {
    const wizardDoc = await Firestore.getDoc(wizardRef);
    const practice = await OrganisationCache.practices.getDoc(
      wizardDoc.practiceRef
    );
    const steps = await Firestore.getDocs(
      PracticeSetupWizard.stepCol(asReffable(wizardRef))
    );
    const definitionMap = new Map(
      PracticeSetupWizard.getRegionFilteredSteps(region).map((step) => [
        step.id,
        step,
      ])
    );

    for (const step of steps.filter(undeletedFilter)) {
      if (step.status === PracticeSetupStepStatus.Complete) {
        continue;
      }

      const stepDefinition = definitionMap.get(step.id);
      if (!stepDefinition) {
        continue;
      }

      try {
        const practice$ = of(practice);
        const validationResult = await snapshot(
          stepDefinition.validate$(practice$, step)
        );

        if (!validationResult) {
          continue;
        }

        const newValidationPassed = validationResult.valid;
        const newValidationErrors = validationResult.valid
          ? undefined
          : validationResult.reason
            ? [validationResult.reason]
            : ['Validation failed'];

        if (
          step.validationPassed !== newValidationPassed ||
          toJSON(step.validationErrors) !== toJSON(newValidationErrors)
        ) {
          await Firestore.patchDoc(step.ref, {
            validationPassed: newValidationPassed,
            validationErrors: newValidationErrors,
          });
        }
      } catch {
        if (!step.validationErrors?.length) {
          await Firestore.patchDoc(step.ref, {
            validationPassed: false,
            validationErrors: ['Validation error occurred'],
          });
        }
      }
    }
  }

  static async resyncSteps(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    region: Region
  ): Promise<{ hasChanges: boolean }> {
    return PracticeSetupWizardStepResync.run(wizardRef, region);
  }

  /**
   * Validates a single step by resolving its definition from the region-
   * filtered registry and invoking the definition's `validate$` observable.
   * Returns a concrete {@link IValidationResult} — falling back to a
   * `{ valid: false }` result when the definition is unknown or validation
   * emits nothing.
   */
  static async validateStep(
    stepId: PracticeSetupStepId,
    practice: WithRef<IPractice>,
    region: Region,
    step?: IPracticeSetupStep,
    options?: IStepValidationOptions
  ): Promise<IValidationResult> {
    const stepDef = PracticeSetupWizard.findStepDefinition(stepId, region);
    if (!stepDef) {
      return { valid: false, reason: `Unknown step: ${stepId}` };
    }
    const result = await snapshot(
      stepDef.validate$(of(practice), step, options)
    );
    return result ?? { valid: false, reason: 'Validation returned no result' };
  }

  static getRegionFilteredSteps(
    region: Region
  ): IPracticeSetupStepDefinition[] {
    return PRACTICE_SETUP_STEPS.filter(
      (step) => !step.regions || step.regions.includes(region)
    );
  }

  static findStepDefinition(
    stepId: PracticeSetupStepId,
    region: Region
  ): IPracticeSetupStepDefinition | undefined {
    return PracticeSetupWizard.getRegionFilteredSteps(region).find(
      ({ id }) => id === stepId
    );
  }

  /**
   * Determines whether a staffer can access this setup wizard using a
   * three-tier check (short-circuiting on the first match):
   *
   * 1. Management users always have access.
   * 2. Staffers explicitly listed in the wizard's `assignedStaff` array.
   * 3. Staffers assigned to any individual (non-deleted) step.
   *
   * The step-level check requires a Firestore read, so it is deferred until
   * the cheaper checks have been exhausted.
   */
  static async hasAccess(
    wizard: WithRef<IPracticeSetupWizard>,
    staffer: IReffable<IStaffer>,
    isManagementUser: boolean
  ): Promise<boolean> {
    if (isManagementUser) {
      return true;
    }

    const userIsAssignedToWizard =
      wizard.assignedStaff?.some((ref) => isSameRef(ref, staffer)) ?? false;

    if (userIsAssignedToWizard) {
      return true;
    }

    const steps = await Firestore.getDocs(PracticeSetupWizard.stepCol(wizard));
    return steps
      .filter(undeletedFilter)
      .some(
        (step) => step.assignedUser && isSameRef(step.assignedUser, staffer)
      );
  }
}
